import React, { useEffect, useRef, useState } from 'react';
import { Button, Spinner, TextInput } from 'flowbite-react';
import { WorkspaceContext } from '../../context/WorkspaceContext';
import * as wasm from 'sched';
import { v4 as uuidv4 } from 'uuid';
import TypeBadge from '../common/TypeBadge';
import { useDatabase } from '../../hooks/useDatabase';

export interface CreatePriorityFormProps {
  onCancel: () => void;
  onSubmit: (node: wasm.NodeMatcher) => void;
  owner: { type_: string; id: string };
  compact?: boolean;
}

type SearchResult = {
  id: wasm.NodeMatcher;
  entityNodeId: any; // { type_: string, id: string }
  title: string;
  description?: string | null;
  projectName?: string | null;
  type: 'Task' | 'Outcome' | 'Project' | 'Tag' | 'WorkflowInstance' | 'Unknown';
};

const CreatePriorityForm: React.FC<CreatePriorityFormProps> = ({ onCancel, onSubmit, owner, compact = false }) => {
  const { safeData } = React.useContext(WorkspaceContext);
  const { applyCommand } = useDatabase();
  const [term, setTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!term || !safeData) {
      setResults([]);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    const handle = setTimeout(async () => {
      try {
        const raw = await wasm.search_priorities(safeData, term.toLowerCase(), 12, false);
        const formatted: SearchResult[] = raw.map((r: wasm.PrioritySearchResultItem) => {
          const id: wasm.NodeMatcher = r.id();
          const entityNodeId = r.entity_node_id();
          const title = r.title();
          const description = r.description();
          const project = r.project?.();
          const projectName = project ? project.name() : null;

          // detect type from entityNodeId
          const nodeType = entityNodeId?.type_ ?? '';
          let type: SearchResult['type'] = 'Unknown';
          if (nodeType === 'Task') type = 'Task';
          else if (nodeType === 'Outcome') type = 'Outcome';
          else if (nodeType === 'Project') type = 'Project';
          else if (nodeType === 'WorkflowInstance') type = 'WorkflowInstance';
          else {
            // fallback: check NodeMatcher shape
            const anyId = id as unknown as Record<string, any>;
            if (anyId.Tag) type = 'Tag';
          }

          return { id, entityNodeId, title, description, projectName, type };
        });
        setResults(formatted);
      } catch (e) {
        console.error('search_priorities failed', e);
        setResults([]);
      } finally {
        setIsSearching(false);
        setHasSearched(true);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [term, safeData]);

  const handleSelect = async (r: SearchResult) => {
    if (r.type === 'WorkflowInstance') {
      // Workflow instances go directly to priority_items via AddPriorityItem command
      try {
        await applyCommand({
          type: 'AddPriorityItem',
          owner,
          id: uuidv4(),
          entity_id: r.entityNodeId,
          position: 'a0', // default position — user can reorder later
        });
      } catch (e) {
        console.error('Failed to add priority item:', e);
      }
      onCancel(); // close form
    } else {
      // Tasks, outcomes, projects, tags go through the existing NodeMatcher flow
      onSubmit(r.id);
    }
  };

  return (
    <div className="space-y-3">
      <TextInput
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        placeholder="Search tasks, outcomes, workflows..."
        autoFocus
      />
      <div ref={dropdownRef} className="max-h-64 overflow-auto rounded border border-cm-border-subtle">
        {isSearching ? (
          <div className="px-4 py-2 text-cm-text-muted flex items-center gap-2"><Spinner size="sm" /> Searching...</div>
        ) : results.length > 0 ? (
          results.map((r, idx) => (
            <div
              key={idx}
              className="px-4 py-2 cursor-pointer hover:bg-cm-bg-hover"
              onClick={() => handleSelect(r)}
            >
              {compact ? (
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <TypeBadge t={r.type} />
                    <span className="text-sm font-medium truncate">{r.title}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3 text-xs text-cm-text-muted">
                    {r.description ? (
                      <span className="truncate max-w-[70%]">{r.description}</span>
                    ) : <span />}
                    <span className="min-w-[100px] text-right truncate">{r.projectName || ''}</span>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2 font-medium">
                      <TypeBadge t={r.type} />
                      <span>{r.title}</span>
                    </div>
                    <div className="text-xs text-cm-text-muted min-w-[100px] text-right">{r.projectName || ''}</div>
                  </div>
                  {r.description && (
                    <div className="text-xs text-cm-text-muted mt-1 line-clamp-1">{r.description}</div>
                  )}
                </>
              )}
            </div>
          ))
        ) : (
          <div className="px-4 py-2 text-cm-text-muted">
            {term && hasSearched ? 'No matching items found' : 'Type to search'}
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button color="light" type="button" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
};

export default CreatePriorityForm;
